import React from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Play, Eye, Clock, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { liveTitle } from "@/lib/liveTitle";
import { compactNumber } from "@/lib/formatNumber";
import { Button } from "@/components/ui/button";
import { fmtDur, livePath } from "@/components/ChurchReplayCard";

// « Prédication vedette » : la rediffusion la plus vue de l'église, en grande carte en tête de page (bouton « Regarder »).
export const ChurchFeaturedReplay = ({ live: l, church, total = 0 }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  if (!l || l.status === "live") return null;
  const title = liveTitle(l, t, { name: church?.name });
  const poster = l.poster_url || l.thumbnail_url;
  const date = l.started_at ? new Date(l.started_at).toLocaleDateString(lang || "fr", { day: "numeric", month: "long", year: "numeric" }) : "";
  const open = () => nav(livePath(l));
  return (
    <div className="yalola-card overflow-hidden" data-testid="church-featured-replay">
      <div className="grid md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <button type="button" onClick={open} className="group relative block w-full aspect-video text-left" style={{ background: "#0A0D0C" }} data-testid="church-featured-replay-poster">
          {poster ? <img src={resolveMediaUrl(poster)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            : <div className="absolute inset-0" style={{ background: "var(--gradient-navy)" }} />}
          <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)" }} />
          <span className="absolute inset-0 grid place-items-center">
            <span className="h-16 w-16 rounded-full grid place-items-center bg-white/90 text-black shadow-xl transition-transform group-hover:scale-110"><Play size={26} className="ml-1" fill="currentColor" /></span>
          </span>
          <span className="absolute top-3 left-3 text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full inline-flex items-center gap-1 shadow" style={{ background: "var(--yalola-accent)", color: "#1F1A12" }} data-testid="church-featured-replay-badge">
            <Flame size={12} fill="currentColor" /> {t("church.featured_replay", "Prédication vedette")}
          </span>
          {l.replay_duration > 0 && <span className="absolute bottom-3 right-3 text-[11px] font-semibold text-white px-1.5 py-0.5 rounded tabular-nums" style={{ background: "rgba(0,0,0,0.75)" }}>{fmtDur(l.replay_duration)}</span>}
        </button>
        <div className="p-4 md:p-6 flex flex-col justify-center gap-3">
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-primary)" }}>{t("church.featured_replay_kicker", "La plus regardée de {church}").replace("{church}", church?.name || "")}</div>
          <h2 className="text-lg md:text-2xl font-bold font-display leading-snug line-clamp-3" data-testid="church-featured-replay-title">{title}</h2>
          <div className="flex items-center gap-3 flex-wrap text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-featured-replay-meta">
            {date && <span>{date}</span>}
            <span className="inline-flex items-center gap-1"><Eye size={13} /> {t("church.views_count", "{n} vue(s)").replace("{n}", compactNumber(l.replay_views || 0))}</span>
            {l.replay_duration > 0 && <span className="inline-flex items-center gap-1"><Clock size={13} /> {fmtDur(l.replay_duration)}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <Button className="rounded-full pill-primary h-11 px-6 text-base font-bold" onClick={open} data-testid="church-featured-replay-watch">
              <Play size={17} className="mr-1.5" fill="currentColor" /> {t("church.watch", "Regarder")}
            </Button>
            {total > 1 && (
              <button type="button" onClick={() => nav(`/churches/${church.id}/replays?sort=views`)} className="inline-flex items-center gap-0.5 text-sm font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid="church-featured-replay-all">
                {t("church.featured_replay_all", "Toutes les rediffusions")} <ChevronRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChurchFeaturedReplay;
